import { tokens } from '@kdk/shared/theme';
import { Box, Paper, Skeleton, Stack } from '@mui/material';
import type { ReactNode } from 'react';

/**
 * 読み込み中のスケルトン部品。実際の表示と同じ枠・同じ行の高さにして、データが届いたときに
 * 画面がずれないようにする。形(影)はすべて aria-hidden で、読み上げは AsyncBoundary 側の「読み込み中…」が担う。
 * 色は MUI の Skeleton が theme から決める。アニメーションは prefers-reduced-motion で theme が止める。
 */

/** MUI の size="small" の表の、本体 1 行の高さ(px)。実際の表の行(文字 + セルの余白 + 罫線)と揃える。 */
export const SKELETON_ROW_HEIGHT = 41;
/** 管理画面の一覧の本体 1 行の高さ(px、実測 47.8)。行に操作ボタン(編集・削除)があり、既定の行より高い。 */
export const SKELETON_ADMIN_ROW_HEIGHT = 48;
/** 管理画面の、件数が多い一覧のスケルトンの行数。枠からはみ出す分は隠れるので、背の高い画面でも埋まる数にする。 */
export const SKELETON_ADMIN_ROWS = 24;
/** 表の見出し行の高さ(px)。 */
export const SKELETON_HEADER_HEIGHT = 41;
/** MUI の TablePagination(フッター)の高さ(px)。 */
export const SKELETON_FOOTER_HEIGHT = 52;

type Breakpoint = 'xs' | 'sm' | 'md';

/** `from` 以上の幅でだけ `active`、それより狭い幅では `base`(sx のブレークポイント指定)。 */
const fromBreakpoint = <T extends string | number>(from: Breakpoint, base: T, active: T) =>
  from === 'xs' ? active : { xs: base, [from]: active };

type BarProps = {
  width?: string | number;
  height?: string | number;
  variant?: 'text' | 'rounded';
};

/** 1 本の棒(文字や入力欄の代わり)。 */
export const SkeletonBar = ({ width = '100%', height = 14, variant = 'rounded' }: BarProps) => (
  <Skeleton
    variant={variant}
    animation="wave"
    width={width}
    height={height}
    sx={{ maxWidth: '100%', flex: 'none' }}
  />
);

type RootProps = { testId: string; children: ReactNode; sx?: Readonly<Record<string, unknown>> };

/** スケルトンの外側。読み上げの対象外(aria-hidden)で、テストでは testid で見つける。 */
const SkeletonRoot = ({ testId, children, sx }: RootProps) => (
  <Box aria-hidden="true" data-testid={testId} sx={{ minWidth: 0, ...sx }}>
    {children}
  </Box>
);

type TableSkeletonProps = {
  /** 各列の幅。数値は比率(fr)、文字列は CSS の grid 列の指定(例: '56px')。実際の表の列の比率に合わせる。 */
  columns: readonly (string | number)[];
  rows?: number;
  rowHeight?: number;
  headerHeight?: number;
  /** 表の下に、ページ送り(TablePagination)の分の帯を付ける。 */
  footer?: boolean;
  /** TableScroll の `fill` と同じ: 残りの高さをすべて受け取る(`fillFrom` 以上の幅)。 */
  fill?: boolean;
  /** TableScroll の `maxHeight` と同じ高さの上限。 */
  maxHeight?: string | number;
  verticalFrom?: Breakpoint;
  /** 'card' は枠線と背景のあるカード(TableScroll の既定と同じ)。'plain' は枠なし。 */
  variant?: 'card' | 'plain';
  minWidth?: number;
  testId?: string;
};

/**
 * 表のスケルトン。TableScroll と同じ外枠(カード・fill・maxHeight)の中に、見出し行と本体の行を並べる。
 * 行は多めに並べて、枠からはみ出す分は隠す(DOM は rows 行ぶんだけ)。
 */
export const TableSkeleton = ({
  columns,
  rows = 12,
  rowHeight = SKELETON_ROW_HEIGHT,
  headerHeight = SKELETON_HEADER_HEIGHT,
  footer = false,
  fill = false,
  maxHeight,
  verticalFrom = 'sm',
  variant = 'card',
  minWidth,
  testId = 'skeleton-table',
}: TableSkeletonProps) => {
  const template = columns.map((c) => (typeof c === 'number' ? `${c}fr` : c)).join(' ');
  const rowSx = {
    display: 'grid',
    gridTemplateColumns: template,
    alignItems: 'center',
    columnGap: '20px',
    px: '10px',
    borderBottom: `1px solid ${tokens.line}`,
    boxSizing: 'border-box',
  } as const;
  return (
    <SkeletonRoot
      testId={testId}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        maxWidth: '100%',
        overflow: 'hidden',
        flex: fill ? fromBreakpoint(verticalFrom, 'none', '1 1 0') : 'none',
        minHeight: 0,
        ...(maxHeight !== undefined && {
          maxHeight: fromBreakpoint(verticalFrom, 'none', maxHeight),
        }),
        ...(variant === 'card' && {
          bgcolor: 'background.paper',
          border: `1px solid ${tokens.line}`,
          borderRadius: 1,
        }),
      }}
    >
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', minWidth: minWidth ?? 0 }}>
        <Box
          data-testid="skeleton-table-header"
          sx={{ ...rowSx, height: headerHeight, bgcolor: tokens.panel2 }}
        >
          {columns.map((_, index) => (
            <SkeletonBar key={index} width="60%" height={12} />
          ))}
        </Box>
        {Array.from({ length: rows }, (_, row) => (
          <Box key={row} data-testid="skeleton-table-row" sx={{ ...rowSx, height: rowHeight }}>
            {columns.map((_, index) => (
              <SkeletonBar key={index} width={index % 3 === 1 ? '90%' : '65%'} />
            ))}
          </Box>
        ))}
      </Box>
      {footer && (
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            justifyContent: 'flex-end',
            flex: 'none',
            height: SKELETON_FOOTER_HEIGHT,
            px: '16px',
            borderTop: `1px solid ${tokens.line}`,
          }}
        >
          <SkeletonBar width={140} />
        </Stack>
      )}
    </SkeletonRoot>
  );
};

type PanelSkeletonProps = {
  /** 本体の高さ(px)。画面幅で変えるときは { xs: 120, md: 280 } のように指定する。 */
  height?: number | Readonly<Record<string, number>>;
  /** 余白などの追加の指定(例: { mb: '17px' })。 */
  sx?: Readonly<Record<string, string | number>>;
  title?: boolean;
  testId?: string;
};

/** 見出し + 本体のある枠(ダッシュボードのパネルなど)。Panel と同じ枠線・余白。 */
export const PanelSkeleton = ({
  height = 180,
  title = true,
  sx,
  testId = 'skeleton-panel',
}: PanelSkeletonProps) => (
  <SkeletonRoot testId={testId} sx={sx}>
    <Paper variant="outlined" sx={{ p: '18px', minWidth: 0 }}>
      {title && (
        <Box sx={{ mb: '12px' }}>
          <SkeletonBar width="40%" height={18} />
        </Box>
      )}
      {typeof height === 'number' ? (
        <SkeletonBar height={height} />
      ) : (
        <Skeleton variant="rounded" animation="wave" sx={{ height, maxWidth: '100%' }} />
      )}
    </Paper>
  </SkeletonRoot>
);

/** 数字のカード(ダッシュボードの KPI)。KpiRow と同じ枠・列数。 */
export const CardsSkeleton = ({
  count = 5,
  compact = false,
  minHeight,
}: {
  count?: number;
  /** 1 枚の高さ(px)の下限。実際のカードの高さに合わせる。数値なら md 以上だけ、{ xs, md } なら画面幅ごと。 */
  minHeight?: number | Readonly<Record<string, number>>;
  /** true なら低いカード(業務マスタの移管先の集計)。 */
  compact?: boolean;
}) => (
  <SkeletonRoot
    testId="skeleton-cards"
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: `repeat(${count}, 1fr)` },
      border: `1px solid ${tokens.line}`,
      borderRadius: '10px',
      overflow: 'hidden',
      mb: '16px',
    }}
  >
    {Array.from({ length: count }, (_, index) => (
      <Stack
        key={index}
        spacing="8px"
        sx={{
          p: compact ? '14px 16px' : '16px 18px',
          ...(minHeight !== undefined && {
            minHeight: typeof minHeight === 'number' ? { md: minHeight } : minHeight,
          }),
          bgcolor: compact ? tokens.panel2 : 'background.paper',
          borderRight: { md: `1px solid ${tokens.line}` },
          borderBottom: { xs: `1px solid ${tokens.line}`, md: 0 },
        }}
      >
        <SkeletonBar width="50%" height={13} />
        <SkeletonBar width="70%" height={compact ? 28 : 36} />
        <SkeletonBar width="80%" height={12} />
      </Stack>
    ))}
  </SkeletonRoot>
);

type ListSkeletonProps = { rows?: number; rowHeight?: number; testId?: string };

/** 縦の一覧(配置修正の左の一覧、ドロワーの中の一覧)。1 行は「主文 + 補足」の 2 段。 */
export const ListSkeleton = ({
  rows = 8,
  rowHeight = 52,
  testId = 'skeleton-list',
}: ListSkeletonProps) => (
  <SkeletonRoot testId={testId} sx={{ overflow: 'hidden' }}>
    {Array.from({ length: rows }, (_, index) => (
      <Stack
        key={index}
        spacing="6px"
        sx={{ justifyContent: 'center', height: rowHeight, px: '12px' }}
      >
        <SkeletonBar width={index % 2 === 0 ? '70%' : '55%'} />
        <SkeletonBar width="30%" height={11} />
      </Stack>
    ))}
  </SkeletonRoot>
);

/** 入力欄の並び(ラベル + 入力欄を縦に積む)。編集フォームや詳細パネルの本体。 */
export const FormSkeleton = ({ fields = 4 }: { fields?: number }) => (
  <SkeletonRoot testId="skeleton-form">
    <Stack spacing="18px">
      {Array.from({ length: fields }, (_, index) => (
        <Stack key={index} spacing="6px">
          <SkeletonBar width="25%" height={12} />
          <SkeletonBar height={40} />
        </Stack>
      ))}
    </Stack>
  </SkeletonRoot>
);

/** 操作行(絞り込みのボタンや入力欄)。`widths` は各部品の幅(px)。 */
export const ToolbarSkeleton = ({
  widths = [200, 160, 160],
  height = 39,
}: {
  widths?: readonly number[];
  height?: number;
}) => (
  <SkeletonRoot
    testId="skeleton-toolbar"
    sx={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center', mb: '12px' }}
  >
    {widths.map((width, index) => (
      <SkeletonBar key={index} width={width} height={height} />
    ))}
  </SkeletonRoot>
);

/** ページの見出し(タイトル + リード文)。PageHeader と同じ下余白。 */
export const PageHeaderSkeleton = () => (
  <SkeletonRoot testId="skeleton-page-header" sx={{ mb: '20px' }}>
    <Stack spacing="8px">
      <SkeletonBar width="min(320px, 70%)" height={30} />
      <SkeletonBar width="min(560px, 100%)" height={14} />
    </Stack>
  </SkeletonRoot>
);

/** 詳細ドロワーの中身(見出し + 項目の並び + 区切りごとのかたまり)。 */
export const DrawerSkeleton = () => (
  <SkeletonRoot testId="skeleton-drawer" sx={{ pt: '14px' }}>
    <Stack spacing="10px">
      <SkeletonBar width="60%" height={26} />
      <SkeletonBar width="35%" height={14} />
    </Stack>
    {[0, 1, 2].map((section) => (
      <Stack
        key={section}
        spacing="10px"
        sx={{ borderTop: `1px solid ${tokens.line}`, pt: '12px', mt: '14px' }}
      >
        <SkeletonBar width="30%" height={14} />
        <SkeletonBar height={section === 1 ? 72 : 14} />
        <SkeletonBar width="80%" height={14} />
      </Stack>
    ))}
  </SkeletonRoot>
);

/** 表の下の注記(小さい文字の数行)。 */
export const NoteSkeleton = ({ lines = 2 }: { lines?: number }) => (
  <SkeletonRoot testId="skeleton-note" sx={{ mt: '10px', flex: 'none' }}>
    {Array.from({ length: lines }, (_, index) => (
      // 実際の注記は 12px の文字 × 行間 1.5 = 1 行 18px
      <Stack key={index} sx={{ justifyContent: 'center', height: '18px' }}>
        <SkeletonBar width={index === lines - 1 ? '60%' : '100%'} height={12} />
      </Stack>
    ))}
  </SkeletonRoot>
);

/** 既定のスケルトン(AsyncBoundary に何も渡さなかったとき)。ごく単純な数本の棒。 */
export const BlockSkeleton = () => (
  <SkeletonRoot testId="skeleton-block">
    <Stack spacing="12px" sx={{ py: '8px' }}>
      <SkeletonBar width="40%" height={18} />
      <SkeletonBar height={14} />
      <SkeletonBar width="85%" height={14} />
      <SkeletonBar width="60%" height={14} />
    </Stack>
  </SkeletonRoot>
);

/** ページ全体(見出し + 操作行 + 表)。LoadingScreen の中や、ページの読み込み待ちに使う。 */
export const PageSkeleton = () => (
  <SkeletonRoot
    testId="skeleton-page"
    sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}
  >
    <PageHeaderSkeleton />
    <ToolbarSkeleton />
    <TableSkeleton fill columns={['64px', '2fr', '1fr', '1fr', '1fr', '1fr']} />
  </SkeletonRoot>
);
